/**
 * Timeline Component
 *
 * Displays a horizontal timeline with:
 * - Time ruler showing seconds/minutes
 * - Beat blocks representing each story beat (width = duration)
 * - Playhead that can be dragged for scrubbing
 * - Play/pause controls
 *
 * TIMELINE CALCULATION:
 * =====================
 * Each beat's position and width is calculated based on:
 * - Beats are sorted by their x-position in the node view
 * - Each beat's width represents its duration (pixels per second)
 * - Beats are displayed sequentially (end-to-end)
 *
 * SCRUBBING:
 * ==========
 * The playhead follows the mouse during drag operations.
 * Mouse position is converted to time using the pixels-per-second ratio.
 */

import { useCallback, useRef, useState, useEffect, useMemo, memo, CSSProperties } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { usePlaybackStore, useCurrentTime, useIsPlaying } from '@/stores/playbackStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { formatTimeWithFrames } from '@/utils/timeUtils';
import { snapToFrame } from '@/utils/frameUtils';
import AudioTrack from '@/components/AudioTrack';
import type { TitleNode } from '@/types';

/**
 * Timeline Component
 */
function Timeline() {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------

  // Ref to the timeline container for calculating positions
  const timelineRef = useRef<HTMLDivElement>(null);

  // Track whether we're currently scrubbing (dragging the playhead)
  const [isScrubbing, setIsScrubbing] = useState(false);

  // Track which beat is being resized (nodeId -> true)
  const [resizingBeatId, setResizingBeatId] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartDuration, setResizeStartDuration] = useState(0);
  
  // Track mouse position and current duration for tooltip
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipDuration, setTooltipDuration] = useState(0);

  // Track beat reordering drag state
  const [draggingBeatId, setDraggingBeatId] = useState<string | null>(null);
  const [dragStartMouseX, setDragStartMouseX] = useState(0);
  const [dragStartMouseY, setDragStartMouseY] = useState(0);
  const [dragStartBeatIndex, setDragStartBeatIndex] = useState(-1);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [ghostPosition, setGhostPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragThresholdMet, setIsDragThresholdMet] = useState(false);
  const [hoveredBeatId, setHoveredBeatId] = useState<string | null>(null);

  // Track title drag/resize state
  const [draggingTitleId, setDraggingTitleId] = useState<string | null>(null);
  const [resizingTitleId, setResizingTitleId] = useState<string | null>(null);
  const [resizeTitleStartX, setResizeTitleStartX] = useState(0);
  const [resizeTitleStartDuration, setResizeTitleStartDuration] = useState(0);
  const [resizeFromLeft, setResizeFromLeft] = useState(false);
  const [dragTitleStartX, setDragTitleStartX] = useState(0);
  const [dragTitleStartTime, setDragTitleStartTime] = useState(0);
  const [hoveredTitleId, setHoveredTitleId] = useState<string | null>(null);
  const titleDragHistorySavedRef = useRef(false);

  // Zoom level - pixels per second (controls timeline zoom)
  const [pixelsPerSecond, setPixelsPerSecond] = useState(50);

  // ---------------------------------------------------------------------------
  // STORE ACCESS
  // ---------------------------------------------------------------------------

  // Project data - subscribe to nodes directly for reactivity
  const nodes = useProjectStore((state) => state.nodes);
  const selectNode = useProjectStore((state) => state.selectNode);
  const selectedNodeIds = useProjectStore((state) => state.selectedNodeIds);
  const updateNode = useProjectStore((state) => state.updateNode);
  const reorderNodeInTimeline = useProjectStore((state) => state.reorderNodeInTimeline);
  const saveHistory = useProjectStore((state) => state.saveHistory);
  const fps = useProjectStore((state) => state.fps);

  // Title nodes
  const titleNodes = useProjectStore((state) => state.titleNodes);
  const selectedTitleIds = useProjectStore((state) => state.selectedTitleIds);
  const addTitleNode = useProjectStore((state) => state.addTitleNode);
  const updateTitleNodeDuration = useProjectStore((state) => state.updateTitleNodeDuration);
  const updateTitleNodePosition = useProjectStore((state) => state.updateTitleNodePosition);
  const deleteTitleNode = useProjectStore((state) => state.deleteTitleNode);
  const deleteAudioTrack = useProjectStore((state) => state.deleteAudioTrack);
  const selectTitleNode = useProjectStore((state) => state.selectTitleNode);
  const clearSelection = useProjectStore((state) => state.clearSelection);
  const clearTitleSelection = useProjectStore((state) => state.clearTitleSelection);
  const duplicateTitleNode = useProjectStore((state) => state.duplicateTitleNode);

  // Audio tracks
  const audioTracks = useProjectStore((state) => state.audioTracks);
  const selectedAudioIds = useProjectStore((state) => state.selectedAudioIds);
  const selectAudioTrack = useProjectStore((state) => state.selectAudioTrack);
  const clearAudioSelection = useProjectStore((state) => state.clearAudioSelection);
  const addAudioTrack = useProjectStore((state) => state.addAudioTrack);
  const showNotification = useNotificationStore((state) => state.showNotification);

  // Playback state
  const currentTime = useCurrentTime();
  const isPlaying = useIsPlaying();
  const setTotalDuration = usePlaybackStore((state) => state.setTotalDuration);
  const setFocusedNodeId = usePlaybackStore((state) => state.setFocusedNodeId);
  const seekTo = usePlaybackStore((state) => state.seekTo);
  const play = usePlaybackStore((state) => state.play);
  const pause = usePlaybackStore((state) => state.pause);
  const goToStart = usePlaybackStore((state) => state.goToStart);
  const goToEnd = usePlaybackStore((state) => state.goToEnd);
  const masterVolume = usePlaybackStore((state) => state.masterVolume);
  const setMasterVolume = usePlaybackStore((state) => state.setMasterVolume);

  // ---------------------------------------------------------------------------
  // CALCULATE BEAT POSITIONS
  // ---------------------------------------------------------------------------

  /**
   * Calculate the timeline representation of each beat
   *
   * Returns an array of beats with their start time, end time, and pixel positions.
   * Subscribes to `nodes` directly so this recalculates when nodes change.
   */
  const timelineBeats = useMemo(() => {
    // Filter out nodes that are on the shelf, then sort by x-position for timeline order
    const filtered = nodes.filter((node) => !node.data.onShelf);
    const sorted = [...filtered].sort((a, b) => a.position.x - b.position.x);
    let currentStart = 0;

    return sorted.map((node) => {
      const startTime = currentStart;
      const endTime = startTime + node.data.duration;
      currentStart = endTime;

      return {
        node,
        startTime,
        endTime,
        startX: startTime * pixelsPerSecond,
        width: node.data.duration * pixelsPerSecond,
      };
    });
  }, [nodes, pixelsPerSecond]);

  // Calculate total duration from timeline beats and titles
  const totalDuration = useMemo(() => {
    let maxTime = 0;
    if (timelineBeats.length > 0) {
      maxTime = timelineBeats[timelineBeats.length - 1].endTime;
    }
    if (titleNodes.length > 0) {
      const titleEndTime = Math.max(...titleNodes.map(t => t.data.startTime + t.data.duration));
      maxTime = Math.max(maxTime, titleEndTime);
    }
    return maxTime;
  }, [timelineBeats, titleNodes]);

  // Calculate title positions for display
  const timelineTitles = useMemo(() => {
    return titleNodes.map((title) => {
      const startX = title.data.startTime * pixelsPerSecond;
      const width = title.data.duration * pixelsPerSecond;
      return { title, startX, width };
    });
  }, [titleNodes, pixelsPerSecond]);

  // Sync total duration to playback store when it changes
  useEffect(() => {
    setTotalDuration(totalDuration);
  }, [totalDuration, setTotalDuration]);

  // Update focused node ID based on current time
  useEffect(() => {
    // Find which beat the playhead is currently on
    const currentBeat = timelineBeats.find(
      (beat) => currentTime >= beat.startTime && currentTime < beat.endTime
    );

    setFocusedNodeId(currentBeat ? currentBeat.node.id : null);
  }, [currentTime, timelineBeats, setFocusedNodeId]);

  // ---------------------------------------------------------------------------
  // SCRUBBING HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Convert a mouse X position to time in seconds
   */
  const xToTime = useCallback((clientX: number) => {
    if (!timelineRef.current) return 0;

    const rect = timelineRef.current.getBoundingClientRect();
    const scrollLeft = timelineRef.current.scrollLeft;
    const x = clientX - rect.left + scrollLeft;

    return Math.max(0, x / pixelsPerSecond);
  }, [pixelsPerSecond]);

  /**
   * Handle mouse down on timeline - start scrubbing
   */
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      // Only left click
      if (event.button !== 0) return;

      // Don't start scrubbing if we clicked on a beat block
      // (beat blocks have their own mousedown handlers that call stopPropagation)
      // This handler only fires when clicking on empty timeline area

      setIsScrubbing(true);
      const time = xToTime(event.clientX);
      seekTo(time);
    },
    [xToTime, seekTo]
  );

  /**
   * Handle mouse move - update playhead position while scrubbing
   */
  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isScrubbing) return;

      const time = xToTime(event.clientX);
      seekTo(time);
    },
    [isScrubbing, xToTime, seekTo]
  );

  /**
   * Handle mouse up - stop scrubbing
   */
  const handleMouseUp = useCallback(() => {
    setIsScrubbing(false);
  }, []);

  // Set up global mouse event listeners for scrubbing
  useEffect(() => {
    if (isScrubbing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isScrubbing, handleMouseMove, handleMouseUp]);

  // ---------------------------------------------------------------------------
  // BEAT RESIZE HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Handle mouse down on beat resize handle - start resizing
   */
  const handleResizeMouseDown = useCallback(
    (event: React.MouseEvent, nodeId: string, currentDuration: number) => {
      // Only left click
      if (event.button !== 0) return;

      // Prevent event from bubbling to timeline scrubbing
      event.stopPropagation();

      // Auto-select the node being resized
      selectNode(nodeId);

      // Save history BEFORE resize starts
      saveHistory();

      setResizingBeatId(nodeId);
      setResizeStartX(event.clientX);
      setResizeStartDuration(currentDuration);
      
      // Initialize tooltip position immediately to prevent flash
      setTooltipPosition({ x: event.clientX, y: event.clientY });
      setTooltipDuration(currentDuration);
    },
    [selectNode, saveHistory]
  );

  /**
   * Handle mouse move - update beat duration while resizing
   */
  const handleResizeMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!resizingBeatId) return;

      const deltaX = event.clientX - resizeStartX;
      const deltaDuration = deltaX / pixelsPerSecond;
      const rawDuration = Math.max(0.5, resizeStartDuration + deltaDuration); // Minimum 0.5 seconds

      // Snap to nearest frame boundary
      const snappedDuration = snapToFrame(rawDuration, fps);

      // Update tooltip position and duration
      setTooltipPosition({ x: event.clientX, y: event.clientY });
      setTooltipDuration(snappedDuration);

      // Update the node's duration
      updateNode(resizingBeatId, { duration: snappedDuration });
    },
    [resizingBeatId, resizeStartX, resizeStartDuration, pixelsPerSecond, updateNode, fps]
  );

  /**
   * Handle mouse up - stop resizing
   */
  const handleResizeMouseUp = useCallback(() => {
    setResizingBeatId(null);
  }, []);

  // Set up global mouse event listeners for beat resizing
  useEffect(() => {
    if (resizingBeatId) {
      window.addEventListener('mousemove', handleResizeMouseMove);
      window.addEventListener('mouseup', handleResizeMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleResizeMouseMove);
        window.removeEventListener('mouseup', handleResizeMouseUp);
      };
    }
  }, [resizingBeatId, handleResizeMouseMove, handleResizeMouseUp]);

  // ---------------------------------------------------------------------------
  // BEAT REORDERING DRAG HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Handle mouse down on beat block - start drag to reorder
   */
  const handleBeatDragStart = useCallback(
    (event: React.MouseEvent, nodeId: string, beatIndex: number, beatWidth: number) => {
      // Only left click
      if (event.button !== 0) return;

      // Check if mouse is on the resize handle (right 8px edge)
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const mouseXInBeat = event.clientX - rect.left;
      const isOnResizeHandle = mouseXInBeat > beatWidth - 8;

      if (isOnResizeHandle) {
        // Let resize handler take over
        return;
      }

      // Prevent event from bubbling to timeline scrubbing
      event.stopPropagation();

      // Record initial state
      setDragStartMouseX(event.clientX);
      setDragStartMouseY(event.clientY);
      setDragStartBeatIndex(beatIndex);
      setDraggingBeatId(nodeId);
      setIsDragThresholdMet(false);
      setDropTargetIndex(null);
      setGhostPosition(null);

      // Auto-select the node being dragged
      selectNode(nodeId);
    },
    [selectNode]
  );

  /**
   * Handle mouse move - update ghost position and drop target
   */
  const handleBeatDragMove = useCallback(
    (event: MouseEvent) => {
      if (!draggingBeatId || !timelineRef.current) return;

      // Check if drag threshold is met (5px movement)
      if (!isDragThresholdMet) {
        const dx = Math.abs(event.clientX - dragStartMouseX);
        const dy = Math.abs(event.clientY - dragStartMouseY);
        
        if (dx > 5 || dy > 5) {
          setIsDragThresholdMet(true);
        } else {
          // Not enough movement yet
          return;
        }
      }

      // Calculate ghost position (follows cursor)
      const rect = timelineRef.current.getBoundingClientRect();
      const scrollLeft = timelineRef.current.scrollLeft;
      const x = event.clientX - rect.left + scrollLeft;
      const y = event.clientY - rect.top;
      setGhostPosition({ x, y });

      // Calculate drop target index based on mouse position
      // Convert mouse X to time, then find which beat slot it's over
      const mouseTime = Math.max(0, x / pixelsPerSecond);

      // Find the drop target index
      let targetIndex = 0;
      for (let i = 0; i < timelineBeats.length; i++) {
        const beat = timelineBeats[i];
        
        // Skip the beat being dragged
        if (beat.node.id === draggingBeatId) continue;

        const beatMidpoint = beat.startTime + (beat.endTime - beat.startTime) / 2;
        
        if (mouseTime < beatMidpoint) {
          // Mouse is before the midpoint of this beat
          // Insert before this beat
          break;
        }
        
        // If we're past this beat's midpoint, increment target index
        targetIndex++;
      }

      setDropTargetIndex(targetIndex);
    },
    [draggingBeatId, isDragThresholdMet, dragStartMouseX, dragStartMouseY, timelineBeats, pixelsPerSecond, saveHistory]
  );

  /**
   * Handle mouse up - perform the reorder
   */
  const handleBeatDragEnd = useCallback(() => {
    if (draggingBeatId && dropTargetIndex !== null && isDragThresholdMet) {
      // Perform the reorder
      reorderNodeInTimeline(draggingBeatId, dropTargetIndex);
    }

    // Clear drag state
    setDraggingBeatId(null);
    setDropTargetIndex(null);
    setGhostPosition(null);
    setIsDragThresholdMet(false);
    setDragStartBeatIndex(-1);
  }, [draggingBeatId, dropTargetIndex, isDragThresholdMet, reorderNodeInTimeline]);

  // Set up global mouse event listeners for beat dragging
  useEffect(() => {
    if (draggingBeatId) {
      window.addEventListener('mousemove', handleBeatDragMove);
      window.addEventListener('mouseup', handleBeatDragEnd);

      return () => {
        window.removeEventListener('mousemove', handleBeatDragMove);
        window.removeEventListener('mouseup', handleBeatDragEnd);
      };
    }
  }, [draggingBeatId, handleBeatDragMove, handleBeatDragEnd]);

  // ---------------------------------------------------------------------------
  // TITLE NODE HANDLERS
  // ---------------------------------------------------------------------------

  const handleAddTitle = useCallback(() => {
    saveHistory();
    const title = addTitleNode(currentTime, 3, { text: 'New Title' });
    selectTitleNode(title.id);
  }, [currentTime, addTitleNode, selectTitleNode, saveHistory]);

  const handleTitleMouseDown = useCallback((event: React.MouseEvent, titleId: string) => {
    if (event.button !== 0) return;

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const mouseXInTitle = event.clientX - rect.left;
    const isOnResizeHandle = mouseXInTitle > rect.width - 8;

    if (isOnResizeHandle) {
      const title = titleNodes.find(t => t.id === titleId);
      if (!title) return;
      event.stopPropagation();
      saveHistory(); // Save state BEFORE resize
      setResizingTitleId(titleId);
      setResizeTitleStartX(event.clientX);
      setResizeTitleStartDuration(title.data.duration);
      setResizeFromLeft(false);
      return;
    }

    const isOnLeftResizeHandle = mouseXInTitle < 8;
    if (isOnLeftResizeHandle) {
      const title = titleNodes.find(t => t.id === titleId);
      if (!title) return;
      event.stopPropagation();
      saveHistory(); // Save state BEFORE resize
      setResizingTitleId(titleId);
      setResizeTitleStartX(event.clientX);
      setResizeTitleStartDuration(title.data.duration);
      setDragTitleStartTime(title.data.startTime);
      setResizeFromLeft(true);
      return;
    }

    event.stopPropagation();
    const title = titleNodes.find(t => t.id === titleId);
    if (!title) return;

    const isAltPressed = event.altKey;
    
    if (isAltPressed) {
      // Alt+drag creates a duplicate, save history for the duplicate creation
      saveHistory();
      titleDragHistorySavedRef.current = true;
      const newTitle = duplicateTitleNode(titleId, title.data.startTime);
      if (newTitle) {
        setDraggingTitleId(newTitle.id);
        setDragTitleStartX(event.clientX);
        setDragTitleStartTime(newTitle.data.startTime);
      }
    } else {
      // Set up drag state - saveHistory will be called on first drag movement
      titleDragHistorySavedRef.current = false;
      clearSelection();
      selectTitleNode(titleId);
      setDraggingTitleId(titleId);
      setDragTitleStartX(event.clientX);
      setDragTitleStartTime(title.data.startTime);
    }
  }, [titleNodes, selectTitleNode, clearSelection, duplicateTitleNode, saveHistory]);

  const handleTitleDragMove = useCallback((event: MouseEvent) => {
    if (!draggingTitleId) return;
    
    // Save history on first actual movement
    if (!titleDragHistorySavedRef.current) {
      const deltaX = Math.abs(event.clientX - dragTitleStartX);
      if (deltaX > 3) { // 3px threshold
        saveHistory();
        titleDragHistorySavedRef.current = true;
      }
    }
    
    const deltaX = event.clientX - dragTitleStartX;
    const deltaTime = deltaX / pixelsPerSecond;
    const newStartTime = Math.max(0, dragTitleStartTime + deltaTime);
    updateTitleNodePosition(draggingTitleId, newStartTime);
  }, [draggingTitleId, dragTitleStartX, dragTitleStartTime, pixelsPerSecond, updateTitleNodePosition, saveHistory]);

  const handleTitleDragEnd = useCallback(() => {
    setDraggingTitleId(null);
    titleDragHistorySavedRef.current = false;
  }, []);

  const handleTitleResizeMove = useCallback((event: MouseEvent) => {
    if (!resizingTitleId) return;
    const deltaX = event.clientX - resizeTitleStartX;
    const deltaDuration = deltaX / pixelsPerSecond;

    if (resizeFromLeft) {
      const rawNewStart = Math.max(0, dragTitleStartTime + deltaDuration);
      const endTime = dragTitleStartTime + resizeTitleStartDuration;
      const newDuration = Math.max(0.5, endTime - rawNewStart);
      const snappedDuration = snapToFrame(newDuration, fps);
      const finalStart = endTime - snappedDuration;
      setTooltipPosition({ x: event.clientX, y: event.clientY });
      setTooltipDuration(snappedDuration);
      updateTitleNodePosition(resizingTitleId, finalStart);
      updateTitleNodeDuration(resizingTitleId, snappedDuration);
    } else {
      const rawDuration = Math.max(0.5, resizeTitleStartDuration + deltaDuration);
      const snappedDuration = snapToFrame(rawDuration, fps);
      setTooltipPosition({ x: event.clientX, y: event.clientY });
      setTooltipDuration(snappedDuration);
      updateTitleNodeDuration(resizingTitleId, snappedDuration);
    }
  }, [resizingTitleId, resizeTitleStartX, resizeTitleStartDuration, dragTitleStartTime, resizeFromLeft, pixelsPerSecond, fps, updateTitleNodeDuration, updateTitleNodePosition]);

  const handleTitleResizeEnd = useCallback(() => {
    setResizingTitleId(null);
    setResizeFromLeft(false);
  }, []);

  useEffect(() => {
    if (draggingTitleId) {
      window.addEventListener('mousemove', handleTitleDragMove);
      window.addEventListener('mouseup', handleTitleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleTitleDragMove);
        window.removeEventListener('mouseup', handleTitleDragEnd);
      };
    }
  }, [draggingTitleId, handleTitleDragMove, handleTitleDragEnd]);

  useEffect(() => {
    if (resizingTitleId) {
      window.addEventListener('mousemove', handleTitleResizeMove);
      window.addEventListener('mouseup', handleTitleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleTitleResizeMove);
        window.removeEventListener('mouseup', handleTitleResizeEnd);
      };
    }
  }, [resizingTitleId, handleTitleResizeMove, handleTitleResizeEnd]);

  const handleTitleClick = useCallback((titleId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    clearSelection();
    clearAudioSelection();
    selectTitleNode(titleId);
  }, [selectTitleNode, clearSelection, clearAudioSelection]);

  // ---------------------------------------------------------------------------
  // AUDIO IMPORT HANDLER
  // ---------------------------------------------------------------------------

  const handleImportAudio = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/mp3,audio/wav,audio/mpeg,audio/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        // Create object URL for the file
        const objectUrl = URL.createObjectURL(file);
        
        // Get audio duration using object URL
        const audio = new Audio();
        audio.preload = 'metadata';
        
        audio.onloadedmetadata = () => {
          const duration = audio.duration;
          
          // Convert to data URL for storage in project
          const reader = new FileReader();
          reader.onload = (event) => {
            const audioData = event.target?.result as string;
            if (audioData) {
              addAudioTrack(audioData, file.name, file.type, duration, currentTime);
              showNotification(`Audio "${file.name}" imported (${Math.round(duration)}s)`, 'info', 3000);
            }
            URL.revokeObjectURL(objectUrl);
          };
          reader.readAsDataURL(file);
        };
        
        audio.onerror = () => {
          // Add track anyway with estimated duration
          const reader = new FileReader();
          reader.onload = (event) => {
            const audioData = event.target?.result as string;
            if (audioData) {
              addAudioTrack(audioData, file.name, file.type, 60, currentTime);
              showNotification(`Audio "${file.name}" imported (duration unknown)`, 'info', 3000);
            }
            URL.revokeObjectURL(objectUrl);
          };
          reader.readAsDataURL(file);
        };
        
        // Set src after setting up event handlers
        audio.src = objectUrl;
      } catch (error) {
        console.error('Import audio error:', error);
        showNotification('Failed to import audio', 'error', 3000);
      }
    };
    input.click();
  }, [addAudioTrack, showNotification, currentTime]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (!(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLTextAreaElement)) {
          // Delete selected title nodes
          if (selectedTitleIds.length > 0) {
            event.preventDefault();
            selectedTitleIds.forEach((id) => deleteTitleNode(id));
          }
          // Delete selected audio tracks
          else if (selectedAudioIds.length > 0) {
            event.preventDefault();
            selectedAudioIds.forEach((id) => deleteAudioTrack(id));
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedTitleIds, deleteTitleNode, selectedAudioIds, deleteAudioTrack]);

  // ---------------------------------------------------------------------------
  // ZOOM CONTROLS
  // ---------------------------------------------------------------------------

  /**
   * Fit timeline to viewport width
   */
  const handleFitToWidth = useCallback(() => {
    if (!timelineRef.current || totalDuration === 0) return;

    const containerWidth = timelineRef.current.clientWidth;
    // Reserve some padding (100px) for overflow
    const availableWidth = containerWidth - 100;
    const optimalPixelsPerSecond = availableWidth / totalDuration;

    // Clamp to reasonable range (10 to 200 pixels per second)
    const clampedPPS = Math.max(10, Math.min(200, optimalPixelsPerSecond));
    setPixelsPerSecond(clampedPPS);
  }, [totalDuration]);

  /**
   * Zoom in (increase pixels per second)
   */
  const handleZoomIn = useCallback(() => {
    setPixelsPerSecond((prev) => Math.min(200, prev * 1.5));
  }, []);

  /**
   * Zoom out (decrease pixels per second)
   */
  const handleZoomOut = useCallback(() => {
    setPixelsPerSecond((prev) => Math.max(10, prev / 1.5));
  }, []);

  // ---------------------------------------------------------------------------
  // BEAT CLICK HANDLER
  // ---------------------------------------------------------------------------

  /**
   * Handle click on a beat block to select it
   */
  const handleBeatClick = useCallback(
    (nodeId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      clearTitleSelection();
      clearAudioSelection();
      selectNode(nodeId);
    },
    [selectNode, clearTitleSelection, clearAudioSelection]
  );

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const containerStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'var(--nb-timeline-bg)',
    userSelect: 'none',
  };

  const controlsStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--nb-space-sm)',
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    borderBottom: '1px solid var(--nb-border-primary)',
    backgroundColor: 'var(--nb-bg-tertiary)',
  };

  const buttonStyles: CSSProperties = {
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    backgroundColor: 'var(--nb-bg-hover)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    color: 'var(--nb-text-primary)',
    cursor: 'pointer',
    fontSize: 'var(--nb-font-size-xs)',
    minWidth: 32, // Fixed width to prevent size jumping when icon changes
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const timeDisplayStyles: CSSProperties = {
    fontFamily: 'var(--nb-font-mono)',
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-primary)',
    minWidth: 100,
  };

  const timelineWrapperStyles: CSSProperties = {
    flex: 1,
    overflowX: 'auto', // Show horizontal scrollbar when needed
    overflowY: 'hidden',
    position: 'relative',
    cursor: draggingBeatId && isDragThresholdMet ? 'grabbing' : 'default',
    // Force scrollbar to always be visible on WebKit browsers
    // @ts-ignore - WebKit-specific CSS properties
    WebkitOverflowScrolling: 'touch',
  };

  const timelineContentStyles: CSSProperties = {
    position: 'relative',
    height: '100%',
    minWidth: Math.max(totalDuration * pixelsPerSecond + 100, 500),
  };

  const rulerStyles: CSSProperties = {
    height: 24,
    borderBottom: '1px solid var(--nb-border-primary)',
    position: 'relative',
  };

  const tracksStyles: CSSProperties = {
    position: 'relative',
    height: 'calc(100% - 24px)',
    backgroundColor: 'var(--nb-timeline-track)',
    display: 'flex',
    flexDirection: 'column',
  };

  const titleTrackStyles: CSSProperties = {
    position: 'relative',
    height: 36,
    borderBottom: '1px solid var(--nb-border-primary)',
    backgroundColor: 'var(--nb-bg-secondary)',
  };

  const audioTrackContainerStyles: CSSProperties = {
    position: 'relative',
    height: audioTracks.length > 0 ? 50 : 0,
    borderBottom: audioTracks.length > 0 ? '1px solid var(--nb-border-primary)' : 'none',
    backgroundColor: 'var(--nb-bg-tertiary)',
    overflow: 'hidden',
  };

  const beatTrackStyles: CSSProperties = {
    position: 'relative',
    flex: 1,
  };

  const playheadStyles: CSSProperties = {
    position: 'absolute',
    top: -24 - (titleNodes.length > 0 ? 36 : 0) - (audioTracks.length > 0 ? 50 : 0), // Cover ruler + title track + audio track if present
    bottom: 0,
    left: currentTime * pixelsPerSecond - 1,
    width: 2,
    backgroundColor: '#aaaaaa',
    zIndex: 10,
    pointerEvents: 'none',
  };

  const playheadHeadStyles: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: -6, // Center over the 2px line (14px total width / 2 - 1px = 6px)
    width: 14,
    height: 14,
    backgroundColor: '#aaaaaa',
    pointerEvents: 'none',
    clipPath: 'polygon(0% 0%, 100% 0%, 100% 60%, 50% 100%, 0% 60%)', // Upside-down house shape (triangle pointing down)
  };

  const tooltipStyles: CSSProperties = {
    position: 'fixed',
    left: tooltipPosition.x + 15,
    top: tooltipPosition.y - 30,
    backgroundColor: 'var(--nb-bg-secondary)',
    border: '1px solid var(--nb-border-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    fontSize: 'var(--nb-font-size-sm)',
    fontFamily: 'var(--nb-font-mono)',
    color: 'var(--nb-text-primary)',
    pointerEvents: 'none',
    zIndex: 10000,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
    whiteSpace: 'nowrap',
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <>
      {/* CSS for resize handle hover effect */}
      <style>{`
        .beat-resize-handle:hover {
          background-color: rgba(255, 255, 255, 0.3) !important;
        }
      `}</style>

      <div style={containerStyles}>
      {/* Transport Controls */}
      <div style={controlsStyles}>
        <button style={buttonStyles} onClick={goToStart} title="Go to start (Home)">
          ⏮
        </button>
        <button
          style={buttonStyles}
          onClick={isPlaying ? pause : play}
          title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>
        <button style={buttonStyles} onClick={goToEnd} title="Go to end (End)">
          ⏭
        </button>

        <div style={timeDisplayStyles}>
          {formatTimeWithFrames(currentTime, fps)} / {formatTimeWithFrames(totalDuration, fps)}
        </div>

        {/* Zoom Controls */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--nb-space-xs)' }}>
          <button style={buttonStyles} onClick={handleZoomOut} title="Zoom out">
            −
          </button>
          <button style={buttonStyles} onClick={handleFitToWidth} title="Fit to width">
            ⟷
          </button>
          <button style={buttonStyles} onClick={handleZoomIn} title="Zoom in">
            +
          </button>
        </div>

        {/* Add Title Button */}
        <div style={{ display: 'flex', gap: 'var(--nb-space-xs)', marginLeft: 'var(--nb-space-sm)', paddingLeft: 'var(--nb-space-sm)', borderLeft: '1px solid var(--nb-border-primary)' }}>
          <button 
            style={{...buttonStyles, minWidth: 'auto', padding: 'var(--nb-space-xs) var(--nb-space-sm)'}} 
            onClick={handleAddTitle} 
            title="Add title overlay at current time"
          >
            + Title
          </button>
        </div>

        {/* Add Audio Button */}
        <div style={{ display: 'flex', gap: 'var(--nb-space-xs)', marginLeft: 'var(--nb-space-sm)', paddingLeft: 'var(--nb-space-sm)', borderLeft: '1px solid var(--nb-border-primary)' }}>
          <button 
            style={{...buttonStyles, minWidth: 'auto', padding: 'var(--nb-space-xs) var(--nb-space-sm)'}} 
            onClick={handleImportAudio} 
            title="Import MP3 or WAV audio file at current time"
          >
            + Audio
          </button>
        </div>

        {/* Master Volume Control */}
        {audioTracks.length > 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 'var(--nb-space-xs)', 
            marginLeft: 'var(--nb-space-sm)', 
            paddingLeft: 'var(--nb-space-sm)', 
            borderLeft: '1px solid var(--nb-border-primary)' 
          }}>
            <style>{`
              .master-volume-slider {
                -webkit-appearance: none;
                appearance: none;
                background: transparent;
                cursor: pointer;
              }
              .master-volume-slider::-webkit-slider-runnable-track {
                background: #555;
                height: 4px;
                border-radius: 2px;
              }
              .master-volume-slider::-moz-range-track {
                background: #555;
                height: 4px;
                border-radius: 2px;
              }
              .master-volume-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                background: #888;
                height: 10px;
                width: 10px;
                border-radius: 50%;
                margin-top: -3px;
                border: none;
              }
              .master-volume-slider::-moz-range-thumb {
                background: #888;
                height: 10px;
                width: 10px;
                border-radius: 50%;
                border: none;
              }
              .master-volume-slider:hover::-webkit-slider-thumb {
                background: #aaa;
              }
              .master-volume-slider:hover::-moz-range-thumb {
                background: #aaa;
              }
            `}</style>
            <span style={{ fontSize: 'var(--nb-font-size-xs)' }}>🔊</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={masterVolume}
              onChange={(e) => setMasterVolume(parseFloat(e.target.value))}
              onMouseUp={(e) => (e.target as HTMLInputElement).blur()}
              tabIndex={-1}
              className="master-volume-slider"
              style={{ width: '60px' }}
              title={`Master Volume: ${Math.round(masterVolume * 100)}%`}
            />
            <span style={{ fontSize: 'var(--nb-font-size-xs)', minWidth: '32px' }}>
              {Math.round(masterVolume * 100)}%
            </span>
          </div>
        )}
      </div>

      {/* Timeline Area */}
      <div
        ref={timelineRef}
        data-timeline="true"
        style={timelineWrapperStyles}
        onMouseDown={handleMouseDown}
      >
        <div style={timelineContentStyles}>
          {/* Time Ruler */}
          <div style={rulerStyles}>
            {/* Render time markers every second */}
            {Array.from({ length: Math.ceil(totalDuration) + 1 }).map((_, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: i * pixelsPerSecond,
                  top: 0,
                  bottom: 0,
                  borderLeft: '1px solid var(--nb-border-secondary)',
                  paddingLeft: 4,
                  fontSize: 'var(--nb-font-size-xs)',
                  color: 'var(--nb-text-muted)',
                }}
              >
                {i}s
              </div>
            ))}
          </div>

          {/* Tracks Container */}
          <div style={tracksStyles}>
            {/* Title Track - Only show if there are titles */}
            {titleNodes.length > 0 && (
              <div style={titleTrackStyles}>
                {timelineTitles.map(({ title, startX, width }) => {
                  const isDragging = draggingTitleId === title.id;
                  const isResizing = resizingTitleId === title.id;
                  const isHovered = hoveredTitleId === title.id;
                  const isSelected = selectedTitleIds.includes(title.id);

                  return (
                    <div
                      key={title.id}
                      onClick={(e) => handleTitleClick(title.id, e)}
                      onMouseDown={(e) => handleTitleMouseDown(e, title.id)}
                      onMouseEnter={() => setHoveredTitleId(title.id)}
                      onMouseLeave={() => setHoveredTitleId(null)}
                      title={isSelected ? 'Drag to move, edges to resize, Alt+drag to duplicate' : 'Click to select'}
                      style={{
                        position: 'absolute',
                        left: startX,
                        top: 4,
                        width: Math.max(width - 2, 4),
                        height: 28,
                        backgroundColor: isSelected ? 'var(--nb-accent-primary)' : isHovered ? 'var(--nb-accent-secondary)' : 'var(--nb-timeline-beat)',
                        borderRadius: 'var(--nb-radius-sm)',
                        cursor: isDragging ? 'grabbing' : (isHovered ? 'move' : 'pointer'),
                        overflow: 'visible',
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: 'var(--nb-space-xs)',
                        paddingRight: 'var(--nb-space-xs)',
                        transition: 'background-color var(--nb-transition-fast)',
                        opacity: isDragging ? 0.5 : 1,
                        zIndex: isDragging || isResizing ? 20 : 5,
                      }}
                    >
                      <span style={{ fontSize: 'var(--nb-font-size-xs)', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none', flex: 1, marginRight: 8 }}>
                        {title.data.text || 'Title'}
                      </span>
                      <div onMouseDown={(e) => { e.stopPropagation(); const t = titleNodes.find(x => x.id === title.id); if (!t) return; saveHistory(); setResizingTitleId(title.id); setResizeTitleStartX(e.clientX); setResizeTitleStartDuration(t.data.duration); setDragTitleStartTime(t.data.startTime); setResizeFromLeft(true); }} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, cursor: 'ew-resize', backgroundColor: isResizing && resizeFromLeft ? 'rgba(255,255,255,0.3)' : 'transparent' }} className="title-resize-handle" />
                      <div onMouseDown={(e) => { e.stopPropagation(); const t = titleNodes.find(x => x.id === title.id); if (!t) return; saveHistory(); setResizingTitleId(title.id); setResizeTitleStartX(e.clientX); setResizeTitleStartDuration(t.data.duration); setResizeFromLeft(false); }} style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'ew-resize', backgroundColor: isResizing && !resizeFromLeft ? 'rgba(255,255,255,0.3)' : 'transparent' }} className="title-resize-handle" />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Audio Tracks */}
            {audioTracks.length > 0 && (
              <div style={audioTrackContainerStyles}>
                {audioTracks.map((track) => (
                  <AudioTrack
                    key={track.id}
                    track={track}
                    pixelsPerSecond={pixelsPerSecond}
                    isSelected={selectedAudioIds.includes(track.id)}
                    onSelect={() => {
                      clearTitleSelection();
                      clearSelection();
                      selectAudioTrack(track.id);
                    }}
                  />
                ))}
              </div>
            )}

            {/* Beat Track */}
            <div style={beatTrackStyles}>
              {/* Beat Blocks */}
            {timelineBeats.map(({ node, startX, width }, index) => {
              const isDragging = draggingBeatId === node.id;
              const isHovered = hoveredBeatId === node.id;
              
              return (
                <div
                  key={node.id}
                  onClick={(e) => handleBeatClick(node.id, e)}
                  onMouseDown={(e) => handleBeatDragStart(e, node.id, index, width)}
                  onMouseEnter={() => setHoveredBeatId(node.id)}
                  onMouseLeave={() => setHoveredBeatId(null)}
                  title="Drag to reorder beat"
                  style={{
                    position: 'absolute',
                    left: startX,
                    top: 8,
                    width: width - 2, // Small gap between beats
                    height: 'calc(100% - 16px)',
                    backgroundColor:
                      selectedNodeIds.includes(node.id)
                        ? 'var(--nb-accent-primary)'
                        : 'var(--nb-timeline-beat)',
                    borderRadius: 'var(--nb-radius-sm)',
                    cursor: isDragging ? 'grabbing' : (isHovered ? 'move' : 'pointer'),
                    overflow: 'visible',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: 'var(--nb-space-xs)',
                    transition: 'background-color var(--nb-transition-fast)',
                    opacity: isDragging && isDragThresholdMet ? 0.3 : 1,
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--nb-font-size-xs)',
                      color: 'white',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      pointerEvents: 'none', // Don't interfere with drag
                    }}
                  >
                    {node.data.title}
                  </span>

                  {/* Resize Handle */}
                  <div
                    onMouseDown={(e) => handleResizeMouseDown(e, node.id, node.data.duration)}
                    onMouseEnter={() => setHoveredBeatId(null)} // Clear hover when on resize handle
                    style={{
                      position: 'absolute',
                      right: -2,
                      top: 0,
                      bottom: 0,
                      width: 8,
                      cursor: 'ew-resize',
                      backgroundColor:
                        resizingBeatId === node.id ? 'var(--nb-accent-primary)' : 'transparent',
                      transition: 'background-color var(--nb-transition-fast)',
                      zIndex: 5,
                    }}
                    title="Drag to resize beat duration"
                    className={draggingBeatId ? '' : 'beat-resize-handle'}
                  />
                </div>
              );
            })}

            {/* Drop Indicator - show insertion point */}
            {isDragThresholdMet && dropTargetIndex !== null && draggingBeatId && (
              <div
                style={{
                  position: 'absolute',
                  left: (() => {
                    // Calculate the x position for the drop indicator
                    // Filter out the dragged beat to get accurate positions
                    const beatsWithoutDragged = timelineBeats.filter(b => b.node.id !== draggingBeatId);
                    
                    if (dropTargetIndex === 0) {
                      // Insert at the beginning
                      return beatsWithoutDragged.length > 0 ? beatsWithoutDragged[0].startX : 0;
                    } else if (dropTargetIndex >= beatsWithoutDragged.length) {
                      // Insert at the end
                      const lastBeat = beatsWithoutDragged[beatsWithoutDragged.length - 1];
                      return lastBeat ? lastBeat.startX + lastBeat.width : 0;
                    } else {
                      // Insert between beats
                      return beatsWithoutDragged[dropTargetIndex].startX;
                    }
                  })(),
                  top: 0,
                  bottom: 0,
                  width: 3,
                  backgroundColor: 'var(--nb-accent-primary)',
                  opacity: 0.7,
                  zIndex: 15,
                  pointerEvents: 'none',
                }}
              />
            )}

            {/* Ghost Beat - follows cursor during drag */}
            {isDragThresholdMet && ghostPosition && draggingBeatId && (() => {
              const draggedBeat = timelineBeats.find(b => b.node.id === draggingBeatId);
              if (!draggedBeat) return null;
              
              // Calculate track height to properly center the ghost
              const trackHeight = timelineRef.current 
                ? timelineRef.current.clientHeight - 24 // Subtract ruler height
                : 100; // Fallback estimate
              
              // Ghost beat height matches regular beats: track height - 16px top/bottom padding
              const ghostBeatHeight = trackHeight - 16;
              
              return (
                <div
                  style={{
                    position: 'absolute',
                    left: ghostPosition.x - (draggedBeat.width / 2), // Center horizontally on cursor
                    top: ghostPosition.y - (ghostBeatHeight / 2) - 24, // Center vertically on cursor (subtract ruler height)
                    width: draggedBeat.width - 2,
                    height: ghostBeatHeight,
                    backgroundColor: selectedNodeIds.includes(draggingBeatId)
                      ? 'var(--nb-accent-primary)'
                      : 'var(--nb-timeline-beat)',
                    borderRadius: 'var(--nb-radius-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: 'var(--nb-space-xs)',
                    opacity: 0.5,
                    pointerEvents: 'none',
                    zIndex: 20,
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--nb-font-size-xs)',
                      color: 'white',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {draggedBeat.node.data.title}
                  </span>
                </div>
              );
            })()}

              {/* Playhead */}
              <div style={playheadStyles}>
                <div style={playheadHeadStyles} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Duration Tooltip - shown while resizing */}
      {(resizingBeatId || resizingTitleId) && (
        <div style={tooltipStyles}>
          {formatTimeWithFrames(tooltipDuration, fps)}
        </div>
      )}
    </div>
    </>
  );
}

export default memo(Timeline);
